import { compressImage } from '@/features/atendimento/lib/compress-image';
import {
  type LibraryMedia,
  type MediaType,
  getMediaUrlById,
} from '@/features/media-library/api';
import {
  useDeleteLibrary,
  useMediaLibrary,
  useRenameLibrary,
  useUploadLibrary,
} from '@/features/media-library/hooks';
import { Button } from '@/shared/components/ui/Button';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { confirm } from '@/shared/lib/confirm';
import { downloadCrossOrigin } from '@/features/atendimento/lib/download-blob';
import {
  Check,
  Download,
  Edit2,
  FileText,
  Image as ImageIcon,
  Link2,
  Loader2,
  Music,
  Pause,
  Play,
  Search,
  Trash2,
  Upload,
  Video,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ApiError } from '@/shared/lib/api-client';

function fmtBytes(n: number | null | undefined): string {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDuration(ms: number | null | undefined): string | null {
  if (!ms) return null;
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

/** Extensão default baseada no mime — usado quando original_name é null. */
function extensionForMime(mime: string): string {
  if (mime.includes('jpeg') || mime.includes('jpg')) return '.jpg';
  if (mime.includes('png')) return '.png';
  if (mime.includes('gif')) return '.gif';
  if (mime.includes('webp')) return '.webp';
  if (mime.includes('opus') || mime.includes('ogg')) return '.ogg';
  if (mime.includes('mp3') || mime.includes('mpeg')) return '.mp3';
  if (mime.includes('wav')) return '.wav';
  if (mime.includes('mp4')) return '.mp4';
  if (mime.includes('pdf')) return '.pdf';
  if (mime.includes('spreadsheetml')) return '.xlsx';
  if (mime.includes('wordprocessingml')) return '.docx';
  return '';
}

const TYPE_OPTIONS: Array<{ id: MediaType | 'all'; label: string }> = [
  { id: 'all', label: 'Todos' },
  { id: 'image', label: 'Imagens' },
  { id: 'audio', label: 'Áudios' },
  { id: 'video', label: 'Vídeos' },
  { id: 'document', label: 'Documentos' },
];

export function MediaLibraryTab() {
  const [q, setQ] = useState('');
  const [type, setType] = useState<MediaType | 'all'>('all');
  const params = useMemo(
    () => ({
      ...(q.trim() && { q: q.trim() }),
      ...(type !== 'all' && { type }),
    }),
    [q, type],
  );
  const list = useMediaLibrary(params);
  const items = useMemo(
    () => list.data?.pages.flatMap((p) => p.items) ?? [],
    [list.data],
  );

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    if (!list.hasNextPage || list.isFetchingNextPage) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) list.fetchNextPage();
      },
      { rootMargin: '200px' },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [list.hasNextPage, list.isFetchingNextPage, list.fetchNextPage, items.length]);

  const [uploadOpen, setUploadOpen] = useState(false);

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--n-900)' }}>
            Galeria de Mídia
          </h2>
          <p style={{ fontSize: '12px', color: 'var(--n-500)', marginTop: '2px' }}>
            Imagens, áudios e documentos disponíveis pra usar nos chatbots.
          </p>
        </div>
        <Button onClick={() => setUploadOpen(true)} className="gap-1.5">
          <Upload size={14} /> Enviar arquivo
        </Button>
      </header>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search
            size={14}
            style={{
              position: 'absolute',
              left: 10,
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--n-400)',
            }}
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome…"
            className="h-9 w-full rounded border border-n-200 bg-n-0 pl-8 pr-3 text-[13px] focus:border-brand-500 focus:outline-none"
          />
        </div>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as MediaType | 'all')}
          className="h-9 rounded border border-n-200 bg-n-0 px-2 text-[13px] focus:border-brand-500 focus:outline-none"
        >
          {TYPE_OPTIONS.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {list.isLoading ? (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <Loader2 size={20} className="animate-spin" style={{ color: 'var(--n-400)' }} />
        </div>
      ) : items.length === 0 ? (
        <div
          style={{
            padding: '40px',
            textAlign: 'center',
            background: 'var(--bg-surface)',
            border: '1px dashed var(--n-300)',
            borderRadius: 'var(--r-md)',
            color: 'var(--n-500)',
            fontSize: '13px',
          }}
        >
          {q || type !== 'all'
            ? 'Nenhuma mídia encontrada com esses filtros.'
            : 'Galeria vazia. Clique em "Enviar arquivo" pra começar.'}
        </div>
      ) : (
        <div
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--n-200)',
            borderRadius: 'var(--r-md)',
            overflow: 'hidden',
          }}
        >
          {items.map((m, i) => (
            <MediaRow key={m.id} media={m} isLast={i === items.length - 1} />
          ))}
          {list.hasNextPage && (
            <div
              ref={sentinelRef}
              style={{
                padding: '14px',
                textAlign: 'center',
                borderTop: '1px solid var(--n-100)',
              }}
            >
              {list.isFetchingNextPage ? (
                <Loader2 size={14} className="animate-spin" style={{ color: 'var(--n-400)' }} />
              ) : (
                <span style={{ fontSize: '11px', color: 'var(--n-400)' }}>Carregar mais…</span>
              )}
            </div>
          )}
        </div>
      )}

      {uploadOpen && <UploadModal onClose={() => setUploadOpen(false)} />}
    </section>
  );
}

function MediaRow({ media, isLast }: { media: LibraryMedia; isLast: boolean }) {
  const [editing, setEditing] = useState(false);
  const [labelDraft, setLabelDraft] = useState(media.label);
  const renameMut = useRenameLibrary();
  const deleteMut = useDeleteLibrary();
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Thumbnail só pra imagem.
  useEffect(() => {
    if (media.type !== 'image') return;
    let cancelled = false;
    getMediaUrlById(media.id)
      .then(({ url }) => {
        if (!cancelled) setPreviewUrl(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [media.id, media.type]);

  const handleSave = async (): Promise<void> => {
    const trimmed = labelDraft.trim();
    if (!trimmed || trimmed === media.label) {
      setEditing(false);
      setLabelDraft(media.label);
      return;
    }
    try {
      await renameMut.mutateAsync({ id: media.id, label: trimmed });
      setEditing(false);
    } catch (err) {
      toast.error('Erro ao renomear', {
        description: err instanceof Error ? err.message : 'Tente novamente',
      });
    }
  };

  const handleDelete = async (): Promise<void> => {
    const ok = await confirm({
      title: 'Excluir mídia?',
      description: `"${media.label}" será removida permanentemente. Não é possível desfazer.`,
      confirmText: 'Excluir',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await deleteMut.mutateAsync(media.id);
    } catch (err) {
      const detail =
        err instanceof ApiError ? err.problem.detail ?? err.problem.title : 'Tente novamente';
      toast.error('Erro ao excluir', { description: detail });
    }
  };

  const [downloading, setDownloading] = useState(false);
  const handleDownload = async (): Promise<void> => {
    setDownloading(true);
    try {
      const { url } = await getMediaUrlById(media.id);
      // Filename de download: original_name preservado se existir, senão
      // deriva do label + extensão por mime.
      const ext = media.original_name?.match(/\.[^.]+$/)?.[0] ?? extensionForMime(media.mime_type);
      const filename = media.original_name ?? `${media.label}${ext}`;
      await downloadCrossOrigin(url, filename);
    } catch (err) {
      toast.error('Erro ao baixar', {
        description: err instanceof Error ? err.message : 'Tente novamente',
      });
    } finally {
      setDownloading(false);
    }
  };

  // Copiar link durável (pra reusar a mídia em eventos de mensagem, chatbot,
  // configs, sistemas externos). O `link` vem pronto da API (HMAC, sem expiração).
  const [copied, setCopied] = useState(false);
  const handleCopyLink = async (): Promise<void> => {
    if (!media.link) return;
    try {
      await navigator.clipboard.writeText(media.link);
      setCopied(true);
      toast.success('Link copiado');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Não foi possível copiar o link', {
        description: 'Copie manualmente ou tente de novo.',
      });
    }
  };

  const Icon = media.type === 'image' ? ImageIcon : media.type === 'audio' ? Music : media.type === 'video' ? Video : FileText;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '8px 12px',
        borderBottom: isLast ? 'none' : '1px solid var(--n-100)',
      }}
    >
      {media.type === 'audio' ? (
        <AudioPreviewButton mediaId={media.id} />
      ) : (
        <div
          style={{
            flexShrink: 0,
            height: '36px',
            width: '36px',
            background: 'var(--n-50)',
            borderRadius: 'var(--r-sm)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {media.type === 'image' && previewUrl ? (
            // biome-ignore lint/a11y/useAltText: thumbnail
            <img
              src={previewUrl}
              alt={media.label}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <Icon size={18} strokeWidth={1.75} style={{ color: 'var(--n-500)' }} />
          )}
        </div>
      )}

      <div style={{ minWidth: 0, flex: 1 }}>
        {editing ? (
          <input
            autoFocus
            value={labelDraft}
            onChange={(e) => setLabelDraft(e.target.value)}
            onBlur={() => void handleSave()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSave();
              if (e.key === 'Escape') {
                setEditing(false);
                setLabelDraft(media.label);
              }
            }}
            className="h-7 w-full rounded border border-brand-400 bg-n-0 px-2 font-semibold text-[13px] focus:outline-none"
          />
        ) : (
          <div
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: 'var(--n-900)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={media.label}
          >
            {media.label}
          </div>
        )}
        <div
          style={{
            fontSize: '11px',
            color: 'var(--n-500)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={media.original_name ?? ''}
        >
          {media.original_name ?? '—'}
        </div>
      </div>

      <span
        style={{
          flexShrink: 0,
          fontSize: '11px',
          color: 'var(--n-500)',
          fontVariantNumeric: 'tabular-nums',
          minWidth: '90px',
          textAlign: 'right',
        }}
      >
        {fmtBytes(media.size_bytes)}
        {fmtDuration(media.duration_ms) ? ` · ${fmtDuration(media.duration_ms)}` : ''}
      </span>

      <div style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
        {media.link && (
          <button
            type="button"
            onClick={() => void handleCopyLink()}
            title="Copiar link"
            className="flex h-7 w-7 items-center justify-center rounded text-n-500 hover:bg-n-100 hover:text-n-700"
          >
            {copied ? <Check size={13} className="text-success-600" /> : <Link2 size={13} />}
          </button>
        )}
        <button
          type="button"
          onClick={() => void handleDownload()}
          disabled={downloading}
          title="Baixar"
          className="flex h-7 w-7 items-center justify-center rounded text-n-500 hover:bg-n-100 hover:text-n-700 disabled:opacity-50"
        >
          {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
        </button>
        <button
          type="button"
          onClick={() => setEditing(true)}
          title="Renomear"
          className="flex h-7 w-7 items-center justify-center rounded text-n-500 hover:bg-n-100 hover:text-n-700"
        >
          <Edit2 size={13} />
        </button>
        <button
          type="button"
          onClick={() => void handleDelete()}
          disabled={deleteMut.isPending}
          title="Excluir"
          className="flex h-7 w-7 items-center justify-center rounded text-n-500 hover:bg-danger-100 hover:text-danger-600 disabled:opacity-50"
        >
          <Trash2 size={13} />
        </button>
      </div>
    </div>
  );
}

function UploadModal({ onClose }: { onClose: () => void }) {
  const [label, setLabel] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadMut = useUploadLibrary();
  // ESC fecha o modal (consistência com demais modais). Sem condição `busy`
  // — ESC sempre tenta fechar; se o user está no meio do upload, o backend
  // termina o request normalmente, só não vê toast de sucesso aqui.
  useEscapeClose(!busy, onClose);

  useEffect(() => {
    if (file && !label) {
      setLabel(file.name.replace(/\.[^.]+$/, ''));
    }
  }, [file, label]);

  const handleSubmit = async (): Promise<void> => {
    if (!file || !label.trim()) return;
    setBusy(true);
    setError(null);
    try {
      let blob: Blob = file;
      let filename = file.name;
      if (file.type.startsWith('image/')) {
        try {
          const compressed = await compressImage(file, { forceJpeg: true });
          if (compressed.blob !== file) {
            blob = compressed.blob;
            if (compressed.blob.type === 'image/jpeg' && !/\.jpe?g$/i.test(filename)) {
              filename = filename.replace(/\.[^.]+$/, '') + '.jpg';
            }
          }
        } catch (err) {
          console.warn('image compression failed; uploading original', err);
        }
      }
      await uploadMut.mutateAsync({ file: blob, label: label.trim(), filename });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha no upload');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-[480px] max-w-full rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-[16px] text-n-900">Enviar arquivo</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex h-8 w-8 items-center justify-center rounded-md text-n-500 hover:bg-n-100 disabled:opacity-50"
            aria-label="Fechar"
          >
            <X size={16} />
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-3.5">
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Nome</span>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex: logo-empresa, audio-boas-vindas"
              maxLength={120}
              autoFocus
              className="block w-full rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-n-900 outline-none focus:border-brand-500"
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Arquivo</span>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="flex items-center justify-between rounded-md border border-n-200 bg-n-0 px-3 py-2 text-[13px] text-left hover:border-brand-300"
            >
              <span className={file ? 'text-n-900' : 'text-n-400'}>
                {file ? file.name : 'Selecionar arquivo do computador…'}
              </span>
              <Upload size={14} className="text-n-500" />
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/*,audio/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="hidden"
            />
            {file && (
              <span className="text-[11px] text-n-500">
                {fmtBytes(file.size)} · {file.type || 'tipo desconhecido'}
              </span>
            )}
          </label>
        </div>

        {error && (
          <div className="mt-3 rounded-md bg-danger-50 px-3 py-2 text-[12px] text-danger-700">
            {error}
          </div>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={busy || !file || !label.trim()}>
            {busy && <Loader2 size={12} className="animate-spin" />}
            Enviar
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Botão play/pause inline pra preview de áudio na lista. Lazy: busca signed
 * URL e instancia <audio> só no primeiro click. Pause cancela playback de
 * outros previews automaticamente (single-active via window event).
 */
function AudioPreviewButton({ mediaId }: { mediaId: number }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  // Single-active: outro player tocando pausa este.
  useEffect(() => {
    const handler = (e: Event): void => {
      const ce = e as CustomEvent<{ id: number }>;
      if (ce.detail.id !== mediaId) {
        audioRef.current?.pause();
        setPlaying(false);
      }
    };
    window.addEventListener('chatplus:audio-preview-play', handler);
    return () => window.removeEventListener('chatplus:audio-preview-play', handler);
  }, [mediaId]);

  const handleToggle = async (): Promise<void> => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        window.dispatchEvent(
          new CustomEvent('chatplus:audio-preview-play', { detail: { id: mediaId } }),
        );
        await audioRef.current.play();
        setPlaying(true);
      } else {
        audioRef.current.pause();
        setPlaying(false);
      }
      return;
    }
    setLoading(true);
    try {
      const { url } = await getMediaUrlById(mediaId);
      const a = new Audio(url);
      a.addEventListener('ended', () => setPlaying(false));
      a.addEventListener('pause', () => setPlaying(false));
      audioRef.current = a;
      window.dispatchEvent(
        new CustomEvent('chatplus:audio-preview-play', { detail: { id: mediaId } }),
      );
      await a.play();
      setPlaying(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void handleToggle()}
      disabled={loading}
      title={playing ? 'Pausar' : 'Reproduzir'}
      className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-pill bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-70"
    >
      {loading ? (
        <Loader2 size={14} className="animate-spin" />
      ) : playing ? (
        <Pause size={14} />
      ) : (
        <Play size={14} className="ml-0.5" />
      )}
    </button>
  );
}
